import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import axios from 'axios';

const API_URL = 'https://api.laur.world/api';
const CHECK_INTERVAL = 30 * 60 * 1000; // 30 minutes in milliseconds

const Dashboard = () => {
  const navigate = useNavigate();
  const [newDomain, setNewDomain] = useState('');
  const [newBrand, setNewBrand] = useState('');
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [domains, setDomains] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddingDomain, setIsAddingDomain] = useState(false);
  const [nextCheck, setNextCheck] = useState(CHECK_INTERVAL);
  const [activeTab, setActiveTab] = useState('dashboard');

  const formatTime = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const getStats = (brandDomains) => {
    const total = brandDomains.length;
    const blocked = brandDomains.filter(d => d.status?.blocked).length;
    const healthy = total - blocked;
    return { total, blocked, healthy };
  };

  const fetchBrands = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/brands`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBrands(response.data);
      if (response.data.length > 0) {
        setSelectedBrand(response.data[0].name);
      }
    } catch (error) {
      toast.error('Failed to fetch brands');
    }
  };

  const fetchDomains = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/domains`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDomains(response.data);
    } catch (error) {
      toast.error('Failed to fetch domains');
    }
  };

  const fetchNextCheck = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/next-check`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const nextCheckTime = new Date(response.data.nextCheck);
      const now = new Date();
      setNextCheck(Math.max(0, nextCheckTime - now));
    } catch (error) {
      toast.error('Failed to fetch next check time');
    }
  };

  useEffect(() => {
    fetchBrands();
    fetchDomains();
    fetchNextCheck(); // Fetch the next check time on load
    const interval = setInterval(fetchDomains, CHECK_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setNextCheck(prev => {
        if (prev <= 1000) {
          return CHECK_INTERVAL;
        }
        return prev - 1000;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    toast.success('Logged out successfully');
    navigate('/');
  };

  const handleAddDomain = async (e) => {
    e.preventDefault();
    if (!newDomain.trim()) {
      toast.error('Please enter a domain');
      return;
    }

    setIsAddingDomain(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/domains`,
        { 
          domain: newDomain.trim(),
          brand: selectedBrand
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Domain added successfully');
      setNewDomain('');
      fetchDomains();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add domain');
    } finally {
      setIsAddingDomain(false);
    }
  };

  const handleAddBrand = async (e) => {
    e.preventDefault();
    if (!newBrand.trim()) {
      toast.error('Please enter a brand name');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/brands`,
        { name: newBrand.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Brand added successfully');
      setBrands([...brands, response.data]);
      setNewBrand('');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add brand');
    }
  };

  const handleDeleteBrand = async (id) => {
    if (!window.confirm('Are you sure you want to delete this brand?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/brands/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Brand deleted successfully');
      fetchBrands();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete brand');
    }
  };

  const handleDeleteDomain = async (id) => {
    if (!window.confirm('Are you sure you want to delete this domain?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/domains/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Domain deleted successfully');
      fetchDomains();
    } catch (error) {
      toast.error('Failed to delete domain');
    }
  };

  // Filter domains by selected brand
  const filteredDomains = domains.filter(domain => domain.brand === selectedBrand);
  const stats = getStats(filteredDomains);

  return (
    <div className="min-h-screen bg-gradient-to-br from-base-200 to-base-300 p-2 sm:p-4">
      <div className="navbar bg-base-100 rounded-box shadow-xl mb-4 flex-col sm:flex-row gap-2 p-2 sm:p-4">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary-content" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
              </svg>
            </div>
            <h1 className="text-xl sm:text-2xl font-bold">Domain Monitor</h1>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 items-center">
          <div className="text-sm bg-base-200 px-3 py-1 rounded-full font-mono">
            Next check: {formatTime(nextCheck)}
          </div>
          <button className="btn btn-ghost btn-sm sm:btn-md gap-2" onClick={handleLogout}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Logout
          </button>
        </div>
      </div>
      
      <div className="container mx-auto max-w-4xl">
        {/* Tabs Navigation */}
        <div className="tabs tabs-boxed bg-base-100 p-1 mb-4">
          <button className={`tab flex-1 ${activeTab === 'dashboard' ? 'tab-active' : ''}`} onClick={() => setActiveTab('dashboard')}>
            Dashboard
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'dashboard' && (
          <div>
            {/* Brand Management */}
            <div className="card bg-base-100 shadow-xl mb-4">
              <div className="card-body p-4">
                <h2 className="card-title text-lg sm:text-xl mb-4 flex gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Manage Brands
                </h2>
                <form onSubmit={handleAddBrand} className="flex flex-col sm:flex-row gap-3 mb-4">
                  <input
                    type="text"
                    placeholder="Enter brand name"
                    className="input input-bordered flex-1"
                    value={newBrand}
                    onChange={(e) => setNewBrand(e.target.value)}
                  />
                  <button 
                    type="submit" 
                    className="btn btn-primary min-w-[120px]"
                  >
                    Add Brand
                  </button>
                </form>
                <div className="max-h-40 overflow-y-auto mt-4 p-2 rounded-lg">
                  <div className="flex flex-wrap gap-2">
                    {brands.map((brand) => (
                      <div key={brand._id} className="badge badge-lg gap-2">
                        {brand.name}
                        <button
                          className="btn btn-ghost btn-xs text-error"
                          onClick={() => handleDeleteBrand(brand._id)}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Brand Tabs */}
            <div className="bg-base-100 p-1 mb-4 rounded-lg">
              <div className="overflow-x-auto">
                <div className="tabs tabs-boxed bg-transparent whitespace-nowrap">
                  {brands.map(brand => (
                    <button
                      key={brand._id}
                      className={`tab ${selectedBrand === brand.name ? 'tab-active' : ''}`}
                      onClick={() => setSelectedBrand(brand.name)}
                    >
                      {brand.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="stats shadow">
                <div className="stat">
                  <div className="stat-title">Total Domains</div>
                  <div className="stat-value">{domains.length}</div>
                </div>
              </div>
              <div className="stats shadow">
                <div className="stat">
                  <div className="stat-title">Blocked Domains</div>
                  <div className="stat-value text-error">
                    {domains.filter(d => d.status?.blocked).length}
                  </div>
                </div>
              </div>
              <div className="stats shadow">
                <div className="stat">
                  <div className="stat-title">Average Response</div>
                  <div className="stat-value text-success">
                    {Math.round(domains.reduce((acc, d) => acc + (d.status?.responseTime || 0), 0) / domains.length || 0)}ms
                  </div>
                </div>
              </div>
            </div>

            {/* Add Domain Card */}
            <div className="card bg-base-100 shadow-xl mb-4">
              <div className="card-body p-4">
                <h2 className="card-title text-lg sm:text-xl mb-4 flex gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add Domain to {selectedBrand}
                </h2>
                <form onSubmit={handleAddDomain}>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1">
                      <div className="join w-full">
                        <div className="join-item bg-base-200 px-3 flex items-center border border-r-0 border-base-300 rounded-l-lg">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                          </svg>
                        </div>
                        <input
                          type="text"
                          placeholder="Enter domain (e.g., example.com)"
                          className="input input-bordered join-item flex-1 w-full rounded-r-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                          value={newDomain}
                          onChange={(e) => setNewDomain(e.target.value)}
                          disabled={isAddingDomain}
                        />
                      </div>
                    </div>
                    <button 
                      type="submit" 
                      className={`btn btn-primary min-w-[120px] ${isAddingDomain ? 'loading' : ''}`}
                      disabled={isAddingDomain}
                    >
                      {!isAddingDomain && (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      )}
                      Add Domain
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Domain Table */}
            <div className="bg-base-100 rounded-lg shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="table table-zebra w-full">
                  <thead>
                    <tr>
                      <th>Domain</th>
                      <th>Brand</th>
                      <th>Status</th>
                      <th>Response Time</th>
                      <th>Last Checked</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {domains.map((domain) => (
                      <tr key={domain._id} className="hover">
                        <td>{domain.domain}</td>
                        <td>{domain.brand}</td>
                        <td>
                          <div className={`badge ${domain.status?.blocked ? 'badge-error' : 'badge-success'}`}>
                            {domain.status?.blocked ? 'Blocked' : 'Active'}
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            <span>{domain.status?.responseTime || 0}ms</span>
                            {domain.status?.responseTime > 1000 && (
                              <div className="tooltip" data-tip="High latency">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-warning" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center gap-1 opacity-80">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {domain.status?.lastChecked 
                              ? new Date(domain.status.lastChecked).toLocaleString()
                              : 'Not checked yet'}
                          </div>
                        </td>
                        <td>
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => handleDeleteDomain(domain._id)}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-error" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Improved Footer */}
      <footer className="footer p-4 text-center mt-10 w-max mx-auto">
        <div className="container mx-auto">
          <p>Contact: <a href="https://t.me/kitaoverdose" className="underline hover:text-yellow-300">Telegram</a></p>
        </div>
      </footer>
    </div>
  );
};

export default Dashboard;
